"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAutoSaveEntity = void 0;
const React = __importStar(require("react"));
const merlin_hooks_1 = require("@travelnest/merlin-hooks");
const react_redux_1 = require("react-redux");
const errorNotifier_1 = require("../errorNotifier");
const viewstate_1 = require("../../entities/viewstate");
const lodash_1 = require("lodash");
const reducer_1 = require("./reducer");
const INTEG_TEST_RUNNING = !!process.env.INTEG_TEST;
/**
 * A hook to power a view that will auto-save changes.
 *
 * Retrieves an entity from the store, optionally loading if it's not there already.
 * Takes in an update function that is dispatched to the store to perform updates, but can be extended to do
 * _anything_ so long as it returns a ThunkAction.
 *
 * @param selector A selector function that is called with the redux store's current state and will retrieve the entity from the store.
 * @param getAction A function that returns a dispatchable action to load the entity if it
 * 		is not present in the store.
 * @param updateAction A function that is called with the entity to be updated, and will return a
 * 		dispatchable action.
 * @param getActionErrorTriggers Redux actions to listen for to indicate that the get call has errored.
 * @param updateActionErrorTriggers Redux actions to listen for to indicate that the update call has errored.
 * @param debounce The amount of time in milliseconds to wait between update invocations.
 *
 * @returns {
 *   entity: T | null,
 *   update: (patch: Partial<T>) => void,
 *   viewState: ViewState
 *   lastSaved: Date | null
 *   getterError: boolean
 *   updaterError: boolean
 * }
 */
const useAutoSaveEntity = (selector, getAction, updateAction, getActionErrorTriggers, updateActionErrorTriggers, 
// Set an arbitrarily high debounce interval when integ-testing, as we control test saves
debounce = INTEG_TEST_RUNNING ? 1000000 : 500) => {
    // Using the get clear _and_ update clear actions here means that if we get valid 404s, any subsequent updates will clear
    // the "getter" error state
    const getActionErrorTriggersClear = [getActionErrorTriggers.clear, updateActionErrorTriggers.clear];
    const getActionErrorTriggersError = [getActionErrorTriggers.error];
    const updateActionErrorTriggersClear = [updateActionErrorTriggers.clear];
    const updateActionErrorTriggersError = [updateActionErrorTriggers.error];
    const getActionError = (0, errorNotifier_1.useActionErrorNotifier)(getActionErrorTriggersClear.flat(), getActionErrorTriggersError.flat());
    const updateActionError = (0, errorNotifier_1.useActionErrorNotifier)(updateActionErrorTriggersClear.flat(), updateActionErrorTriggersError.flat());
    const { dispatch } = (0, react_redux_1.useStore)();
    const storeEntity = (0, react_redux_1.useSelector)(selector);
    const previousStoreEntity = (0, merlin_hooks_1.usePrevious)(storeEntity);
    const [saving, setSaving] = React.useState(false);
    const [localEntity, entityDispatch] = React.useReducer(reducer_1.autoSaveEntityReducer, (0, lodash_1.cloneDeep)(storeEntity));
    const [lastSaved, setLastSaved] = React.useState(null);
    const loading = (0, merlin_hooks_1.useDataLoad)(storeEntity === null || storeEntity === undefined, () => __awaiter(void 0, void 0, void 0, function* () { return yield dispatch(getAction()); }));
    const debouncedSave = (0, merlin_hooks_1.useDebouncedCallback)((entity) => __awaiter(void 0, void 0, void 0, function* () {
        if (entity) {
            setSaving(true);
            yield dispatch(updateAction(entity));
            setSaving(false);
            setLastSaved(new Date(Date.now()));
        }
    }), debounce);
    // If we are running integ tests, we use this hook to attach the
    // save fn to the window so we can programatically control when data is
    // updated.
    useSaveFnWindowAttachment(debouncedSave);
    React.useEffect(() => {
        // https://reactjs.org/docs/hooks-reference.html#usememo
        // "You may rely on useMemo as a performance optimization, not as a semantic guarantee."
        if (!(0, lodash_1.isEqual)(storeEntity, previousStoreEntity)) {
            // Use deep equality to determine if we need to update the local entity.
            // Works around the storeEntity not providing reference-equal objects
            if (!(0, lodash_1.isEqual)(storeEntity, localEntity)) {
                // Deeply clone the object so that no references remain
                if (storeEntity !== null) {
                    if (previousStoreEntity === null || previousStoreEntity === undefined) {
                        entityDispatch({ type: 'LOAD', payload: (0, lodash_1.cloneDeep)(storeEntity) });
                    }
                    else {
                        entityDispatch({ type: 'UPDATE_SYNC', payload: (0, lodash_1.cloneDeep)(storeEntity) });
                    }
                }
            }
        }
    }, [storeEntity]);
    const update = (patch) => {
        let updated = null;
        if (Array.isArray(patch)) {
            updated = patch;
        }
        else { // Object
            updated = Object.assign(Object.assign({}, localEntity), patch);
        }
        entityDispatch({ type: 'UPDATE_INPUT', payload: (0, lodash_1.cloneDeep)(updated) });
        debouncedSave(updated);
    };
    return {
        entity: localEntity,
        update,
        viewState: (0, viewstate_1.getViewState)(saving, loading),
        lastSaved,
        getterError: getActionError,
        updaterError: updateActionError
    };
};
exports.useAutoSaveEntity = useAutoSaveEntity;
// Equivalent in <root>/cypress/support/commands/flushDebounced.ts
const WINDOW_AUTO_SAVS_FNS_KEY = 'autoSaveFns';
// For integration tests, attach the debouncedSave fn to the window object
// So that it can be programatically controlled
const useSaveFnWindowAttachment = (saveFn) => {
    const key = (Math.random() * 100).toString(16);
    React.useEffect(() => {
        if (INTEG_TEST_RUNNING) {
            console.debug('Attaching debounced update function to the window object');
            if ('autoSaveFns' in window) {
                window[WINDOW_AUTO_SAVS_FNS_KEY][key] = saveFn;
            }
            else {
                window[WINDOW_AUTO_SAVS_FNS_KEY] = { [key]: saveFn };
            }
        }
        return () => {
            if (INTEG_TEST_RUNNING) {
                delete window[WINDOW_AUTO_SAVS_FNS_KEY][key];
            }
        };
    }, []);
};
//# sourceMappingURL=base.js.map